import { useEffect, useMemo, useState } from 'react';
import { Download, Printer, Search, FileText, Eye } from 'lucide-react';

// Admin grid of 27 marketing posters. Each card lets the admin preview, print,
// or download the per-course Letter-portrait PDF generated server-side.
const MarketingPostersPanel = ({ apiBase }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(`${apiBase}/api/admin/marketing-posters`);
        const data = await r.json();
        if (active) setItems(Array.isArray(data) ? data : []);
      } catch (_e) {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [apiBase]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (i) =>
        i.id.toLowerCase().includes(needle) ||
        (i.title || '').toLowerCase().includes(needle) ||
        (i.category || '').toLowerCase().includes(needle)
    );
  }, [items, q]);

  const handlePreview = (id) => {
    window.open(`${apiBase}/api/marketing-poster/${id}`, '_blank', 'noopener');
  };

  const handleDownload = async (id) => {
    try {
      const r = await fetch(`${apiBase}/api/marketing-poster/${id}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medcare-poster-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      // Fallback — open in new tab
      handlePreview(id);
    }
  };

  return (
    <div data-testid="marketing-posters-section">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, marginBottom: 8, color: '#1e3a5f' }}>
          <FileText size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Marketing Posters
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9375rem', margin: 0 }}>
          Print-ready 8.5"×11" PDF marketing posters for every MedCare program.
          Each poster includes the program tagline, key bullets, tuition box, QR-code
          enrollment link, and the MedCare seal. Hand them out at hiring fairs,
          employer events, or post on community boards.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 300px', maxWidth: 420 }}>
          <Search
            size={16}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, code, or category…"
            data-testid="poster-search-input"
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              fontSize: '0.9rem',
            }}
          />
        </div>
        <div style={{ alignSelf: 'center', color: '#64748b', fontSize: '0.85rem' }} data-testid="poster-count-label">
          Showing <strong>{filtered.length}</strong> of <strong>{items.length}</strong> programs
        </div>
      </div>

      {loading && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading marketing posters…</div>
      )}

      {/* Featured: Hire-a-CNA service poster (not part of the course catalog).
          Generated by a dedicated backend endpoint so it can carry its own
          $25/hr pricing, services list, and /hire-cna QR code. */}
      {!loading && (
        <div
          data-testid="poster-hire-cna-featured"
          style={{
            background: 'linear-gradient(135deg,#0d9488,#0f766e)',
            color: 'white',
            borderRadius: 14,
            padding: 18,
            marginBottom: 18,
            display: 'flex',
            gap: 16,
            alignItems: 'center',
            flexWrap: 'wrap',
            boxShadow: '0 6px 18px -10px rgba(15,118,110,0.6)',
          }}
        >
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700, color: '#fde68a' }}>
              In-Home Services · Featured
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: 4 }}>
              MedCare Nursing &amp; Transportation Services
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.92, marginTop: 2 }}>
              Hire a Certified Nurse Assistant · $25/hr · 4-hr min · 24-hr notice ·
              <strong> /hire-cna</strong> QR built-in
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => window.open(`${apiBase}/api/hire-cna/poster.pdf`, '_blank', 'noopener')}
              data-testid="poster-preview-hire-cna"
              style={{
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Eye size={14} /> Preview
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await fetch(`${apiBase}/api/hire-cna/poster.pdf`);
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'medcare-hire-cna-poster.pdf';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (_e) {
                  window.open(`${apiBase}/api/hire-cna/poster.pdf`, '_blank', 'noopener');
                }
              }}
              data-testid="poster-download-hire-cna"
              style={{
                background: '#fbbf24',
                color: '#0b2545',
                border: 'none',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Download size={14} /> Download Poster
            </button>
            <button
              type="button"
              onClick={() => window.open(`${apiBase}/api/hire-cna/qr-code.png`, '_blank', 'noopener')}
              data-testid="poster-qr-hire-cna"
              style={{
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Download size={14} /> QR PNG
            </button>
          </div>
        </div>
      )}

      {!loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }} data-testid="poster-grid">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`poster-card-${c.id}`}
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                boxShadow: '0 2px 6px -2px rgba(15,23,42,0.08)',
              }}
            >
              <div style={{
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                fontWeight: 700,
                color: '#b45309',
              }}>{c.category}</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0b2545', lineHeight: 1.3, minHeight: '2.6em' }}>
                {c.title}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontFamily: 'ui-monospace, "SF Mono", monospace' }}>{c.id}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                <button
                  type="button"
                  onClick={() => handlePreview(c.id)}
                  data-testid={`poster-preview-${c.id}`}
                  style={{
                    flex: 1,
                    background: '#f1f5f9',
                    color: '#0b2545',
                    border: '1px solid #cbd5e1',
                    padding: '8px 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <Eye size={14} /> Preview
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(c.id)}
                  data-testid={`poster-download-${c.id}`}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg,#b45309,#7c2d12)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <Download size={14} /> Download
                </button>
                <button
                  type="button"
                  onClick={() => handlePreview(c.id)}
                  data-testid={`poster-print-${c.id}`}
                  title="Open in new tab to print"
                  style={{
                    background: '#0b2545',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Printer size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketingPostersPanel;
